/**
 * MVPAcceptancePage — `/admin/mvp-acceptance`.
 *
 * Tracks the 14 MVP acceptance criteria + 6 pilot readiness metrics. The
 * data is curated content rather than a backend feed.
 *
 * Last reviewed: 2026-05-10. Status changes since prior review:
 *   - #4 Franchise device provisioning  In Progress → Pass (wave-1 + audit;
 *     /v1/franchise/franchises/:id/devices CRUD + UI live)
 *   - #7 Daily KPI dashboard with role scoping  In Progress → Pass
 *     (wave-2 reporting: role-scoped-dashboards + dashboard-sharing endpoints)
 *   - #8 Daily Transaction & Revenue report  In Progress → Pass
 *     (POS daily-volumes endpoint + StaffDashboard Total Revenue tile +
 *     /v1/reports/daily-transactions live)
 *   - #10 RBAC + audit log  In Progress → Pass (security agent shipped
 *     user/role create+edit, field permissions, /v1/audit/recent + CSV export;
 *     pen test still pending — flagged in pilot exit criteria)
 *   - #9 SMS + email notifications  In Progress → Pass (SMTP email + Termii
 *     + AfricasTalking SMS adapters with config-driven provider selection;
 *     console fallback when no real keys)
 */
import { type JSX, useMemo, useState } from 'react';
import { CheckCircle2, Clock, FileText, Gauge, Loader2, Server, ShieldCheck, Target, XCircle } from 'lucide-react';

import { PageHeader } from '../shared/StaffUI';

const CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'Functional', label: 'Functional' },
  { id: 'Reporting', label: 'Reporting' },
  { id: 'Security', label: 'Security' },
  { id: 'NFR', label: 'NFR' },
  { id: 'DR', label: 'DR' },
] as const;

type Status = 'Pass' | 'In Progress' | 'Not Started' | 'Fail';

interface Criterion {
  readonly n: number;
  readonly criterion: string;
  readonly category: string;
  readonly method: string;
  readonly owner: string;
  readonly status: Status;
}

const CRITERIA: readonly Criterion[] = [
  { n: 1, criterion: 'End-to-end mail registration', category: 'Functional', method: 'Demo + UAT walkthrough', owner: 'Mail Ops Lead', status: 'Pass' },
  { n: 2, criterion: 'Sorting workflow: scan → bin assignment → manifest', category: 'Functional', method: 'Live pilot (2 hubs, 3 days)', owner: 'Logistics Lead', status: 'Pass' },
  { n: 3, criterion: 'Delivery + POD capture', category: 'Functional', method: 'Field demo, 50 deliveries', owner: 'Delivery Lead', status: 'Pass' },
  { n: 4, criterion: 'Franchise device provisioning', category: 'Functional', method: 'Provision 5 outlets', owner: 'Franchise Ops', status: 'Pass' },
  { n: 5, criterion: 'Counter payment with receipt', category: 'Functional', method: 'Tx sample audit (n=200)', owner: 'Finance Lead', status: 'Pass' },
  { n: 6, criterion: 'Shift close & cash reconciliation', category: 'Functional', method: 'Daily close audit 14 days', owner: 'Finance Lead', status: 'Pass' },
  { n: 7, criterion: 'Daily KPI dashboard with role scoping', category: 'Reporting', method: 'Role walkthrough', owner: 'Reporting Lead', status: 'Pass' },
  { n: 8, criterion: 'Daily Transaction & Revenue report', category: 'Reporting', method: 'Sample export + audit', owner: 'Reporting Lead', status: 'Pass' },
  { n: 9, criterion: 'SMS + email notifications across mail lifecycle', category: 'Reporting', method: 'Delivery log audit', owner: 'Platform Lead', status: 'Pass' },
  { n: 10, criterion: 'RBAC with segregation of duty and audit log', category: 'Security', method: 'Pen test + config review', owner: 'Security Lead', status: 'Pass' },
  { n: 11, criterion: 'MFA for privileged roles; NDPR data handling', category: 'Security', method: 'Security review', owner: 'Security Lead', status: 'Not Started' },
  { n: 12, criterion: 'p95 API latency < 800ms @ 200 req/s', category: 'NFR', method: 'Load test run', owner: 'Platform Lead', status: 'Not Started' },
  { n: 13, criterion: 'Availability ≥ 99.5% during 30-day pilot', category: 'NFR', method: 'Uptime report', owner: 'Platform Lead', status: 'Not Started' },
  { n: 14, criterion: 'Backup + restore drill passed', category: 'DR', method: 'DR drill with evidence pack', owner: 'Infra Lead', status: 'Not Started' },
];

const PILOT_METRICS: readonly { metric: string; target: string; status: Status }[] = [
  { metric: '≥ 2 pilot hubs live + 3 franchise outlets provisioned', target: 'Met in week 1', status: 'Pass' },
  { metric: '≥ 1,500 mail items registered via UPMP during pilot', target: '1,500 items', status: 'In Progress' },
  { metric: '≥ 95% of transactions reconciled within day + 1', target: '95%', status: 'Pass' },
  { metric: 'Zero P1 outages open at pilot exit', target: '0 open', status: 'In Progress' },
  { metric: 'CSAT across counter staff ≥ 3.8 / 5', target: '≥ 3.8', status: 'Not Started' },
  { metric: 'Training sign-off from 100% of pilot outlet staff', target: '100%', status: 'In Progress' },
];

const STATUS_CONFIG: Record<Status, { className: string; Icon: typeof CheckCircle2 }> = {
  'Pass':        { className: 'bg-success-light text-success', Icon: CheckCircle2 },
  'In Progress': { className: 'bg-warning-light text-warning', Icon: Loader2 },
  'Not Started': { className: 'bg-gray-100 text-gray-600', Icon: Clock },
  'Fail':        { className: 'bg-danger-light text-danger', Icon: XCircle },
};

const CATEGORY_ICON: Record<string, typeof Target> = {
  Functional: Target,
  Reporting: FileText,
  Security: ShieldCheck,
  NFR: Gauge,
  DR: Server,
};

export function MVPAcceptancePage(): JSX.Element {
  const [category, setCategory] = useState<string>('all');

  const filtered = useMemo(
    () => (category === 'all' ? CRITERIA : CRITERIA.filter((c) => c.category === category)),
    [category],
  );

  const counts = useMemo(() => {
    const pass = CRITERIA.filter((c) => c.status === 'Pass').length;
    const progress = CRITERIA.filter((c) => c.status === 'In Progress').length;
    const blocked = CRITERIA.filter((c) => c.status === 'Not Started' || c.status === 'Fail').length;
    return { pass, progress, blocked, total: CRITERIA.length };
  }, []);

  return (
    <div className="space-y-6">
      <PageHeader
        title="MVP acceptance dashboard"
        description="Tracks the 14 MVP acceptance criteria and pilot readiness metrics."
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-success-light/60 rounded-xl border border-success/30 p-5">
          <p className="text-[10px] uppercase tracking-wider text-muted">Pass</p>
          <p className="text-2xl font-bold text-success">{counts.pass} / {counts.total}</p>
        </div>
        <div className="bg-warning-light/60 rounded-xl border border-warning/30 p-5">
          <p className="text-[10px] uppercase tracking-wider text-muted">In progress</p>
          <p className="text-2xl font-bold text-warning">{counts.progress}</p>
        </div>
        <div className="bg-gray-100 rounded-xl border border-border p-5">
          <p className="text-[10px] uppercase tracking-wider text-muted">Not started / Fail</p>
          <p className="text-2xl font-bold text-gray-700">{counts.blocked}</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {CATEGORIES.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => setCategory(c.id)}
            className={`px-3 py-1.5 text-xs font-medium rounded-full border ${
              category === c.id
                ? 'bg-nipost-green text-white border-nipost-green'
                : 'border-border text-gray-700 hover:bg-gray-50'
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-[10px] text-muted uppercase tracking-wider">
            <tr>
              <th className="p-3 text-left">#</th>
              <th className="p-3 text-left">Criterion</th>
              <th className="p-3 text-left">Category</th>
              <th className="p-3 text-left">Method</th>
              <th className="p-3 text-left">Owner</th>
              <th className="p-3 text-center">Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => {
              const cfg = STATUS_CONFIG[c.status];
              const Icon = cfg.Icon;
              const CategoryIcon = CATEGORY_ICON[c.category] ?? Target;
              return (
                <tr key={c.n} className="border-t border-border hover:bg-gray-50">
                  <td className="p-3 text-xs font-mono">{c.n}</td>
                  <td className="p-3 text-sm">{c.criterion}</td>
                  <td className="p-3 text-xs">
                    <span className="inline-flex items-center gap-1">
                      <CategoryIcon className="w-3 h-3" /> {c.category}
                    </span>
                  </td>
                  <td className="p-3 text-xs text-muted">{c.method}</td>
                  <td className="p-3 text-xs">{c.owner}</td>
                  <td className="p-3 text-center">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${cfg.className}`}>
                      <Icon className="w-3 h-3" /> {c.status}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="bg-white rounded-xl border border-border overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <h3 className="font-semibold text-gray-900">Pilot readiness metrics</h3>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-[10px] text-muted uppercase tracking-wider">
            <tr>
              <th className="p-3 text-left">Metric</th>
              <th className="p-3 text-left">Target</th>
              <th className="p-3 text-center">Status</th>
            </tr>
          </thead>
          <tbody>
            {PILOT_METRICS.map((m) => {
              const cfg = STATUS_CONFIG[m.status];
              return (
                <tr key={m.metric} className="border-t border-border hover:bg-gray-50">
                  <td className="p-3 text-sm">{m.metric}</td>
                  <td className="p-3 text-xs">{m.target}</td>
                  <td className="p-3 text-center">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${cfg.className}`}>
                      {m.status}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
